import { entero, fechaCorta, fechaHora, hora, pct } from "../cc-formato";

/**
 * Calidad de código: cómo se lee una serie del historial (cobertura,
 * duplicación o hallazgos abiertos) en palabras de cualquiera. Funciones
 * puras, sin Angular ni Chart.js: las marcas del eje de fechas (sin repetir
 * el día cuando hay varios análisis la misma fecha), el techo del eje y lo que
 * dice el globo de cada punto.
 */

/** Cómo se leen los valores de la serie. */
export type FormatoSerieCc = "PCT" | "ENTERO";

/** Hacia dónde se cumple la meta: cobertura «80 % o más», duplicación «3 % o menos». */
export type SentidoMetaCc = "MAS" | "MENOS";

/** Lo que se sabe de cada análisis de la serie, además de su valor: su gate y, si ya llegó, versión y commit. */
export interface PuntoSerieCc {
  /** OK | ERROR | NONE. */
  gate: string;
  version: string;
  commit: string;
}

/** Una línea del globo con su tono: is-mejor, is-peor, is-pasa, is-no-pasa o is-neutro. */
export interface LineaGlobo {
  texto: string;
  clase: string;
  icono: string;
}

/** Lo que dice el globo de un punto, de arriba abajo. */
export interface TextoGlobo {
  /** "Análisis del 08/10/2026 08:07". */
  titulo: string;
  /** "Cobertura: 98.5 %". */
  valor: string;
  cambio: LineaGlobo | null;
  meta: LineaGlobo | null;
  gate: LineaGlobo | null;
  /** "Versión 0.0 · commit a707a2e"; vacío sin dato. */
  datos: string;
}

/** La serie y su contexto, como los recibe la gráfica. */
export interface SerieCc {
  nombre: string;
  fechas: string[];
  valores: (number | null)[];
  formato: FormatoSerieCc;
  /** La meta del Quality Gate; null si no la pide. */
  meta: number | null;
  sentido: SentidoMetaCc;
  /** true si bajar es mejorar (duplicación, hallazgos). */
  menosEsMejor: boolean;
  /** "hallazgos": lo que cuenta una serie de enteros, para el globo. */
  unidad: string;
  puntos: (PuntoSerieCc | null | undefined)[];
}

/** Lo que se estima que mide una etiqueta del eje por carácter (12 px) y su aire, en píxeles. */
const PX_POR_CARACTER = 7;
const AIRE_ETIQUETA = 14;

/** El signo de porciento al final, con el espacio fino que pone pct(). */
const SIGNO_PCT = /\s%$/u;
/** El «.0» de un porcentaje redondo, justo antes del signo. */
const CERO_DECIMAL = /\.0(?=\s%$)/u;

/** "80.0 %" → "80 %": un porcentaje redondo, sin el «.0» (en la meta y en el eje). */
export function sinCeroDecimal(texto: string): string {
  return texto.replace(CERO_DECIMAL, "");
}

/** El valor como se lee: "98.5 %" o "6,522". */
export function valorSerie(v: number | null | undefined, formato: FormatoSerieCc, vacio = "sin dato"): string {
  return formato === "PCT" ? pct(v, vacio) : entero(v, vacio);
}

/** La meta en palabras: "Meta: 80 % o más". */
export function textoMeta(meta: number, formato: FormatoSerieCc, sentido: SentidoMetaCc): string {
  return `Meta: ${sinCeroDecimal(valorSerie(meta, formato))} o ${sentido === "MAS" ? "más" : "menos"}`;
}

/** true si el valor cumple la meta en su sentido. */
export function cumpleMeta(v: number, meta: number, sentido: SentidoMetaCc): boolean {
  return sentido === "MAS" ? v >= meta : v <= meta;
}

/**
 * Las marcas del eje de fechas: el índice del punto y lo que dice. Cada día se
 * marca una sola vez (nunca «08/10 08/10 08/10»), en su primer análisis o, si
 * ahí se encimaría con la marca anterior, en otro análisis del mismo día; solo
 * las que caben a lo ancho, siempre con la primera y, si cabe, la más
 * reciente. Si todos los análisis son del mismo día, las marcas son horas. Con
 * más de un año en la serie, la fecha lleva el año corto.
 */
export function marcasDeFechas(fechas: string[], anchoPx: number): Map<number, string> {
  const marcas = new Map<number, string>();
  if (fechas.length === 0) {
    return marcas;
  }
  const dias = fechas.map((f) => fechaCorta(f, ""));
  const unSoloDia = dias.every((d) => d === dias[0]);
  const conAnio = new Set(dias.map((d) => d.slice(6))).size > 1;
  const etiqueta = (i: number): string => {
    if (unSoloDia) {
      return i === 0 ? `${dias[0].slice(0, 5)} ${hora(fechas[0], "")}` : hora(fechas[i], "");
    }
    return conAnio ? `${dias[i].slice(0, 6)}${dias[i].slice(8)}` : dias[i].slice(0, 5);
  };
  // Un grupo por día (o por análisis, si todos son del mismo día): del primer índice al último.
  const grupos: { inicio: number; fin: number }[] = [];
  dias.forEach((d, i) => {
    const ultimo = grupos.at(-1);
    if (ultimo && !unSoloDia && d === dias[i - 1]) {
      ultimo.fin = i;
    } else {
      grupos.push({ inicio: i, fin: i });
    }
  });
  const ancho = Math.max(...grupos.map((g) => etiqueta(g.inicio).length)) * PX_POR_CARACTER + AIRE_ETIQUETA;
  const pxPorPunto = Math.max(1, anchoPx / Math.max(1, fechas.length - 1));
  const separacion = Math.max(1, Math.ceil(ancho / pxPorPunto));
  const coloca = (g: { inicio: number; fin: number }, previo: number | undefined): number | null => {
    const posicion = previo === undefined ? g.inicio : Math.max(g.inicio, previo + separacion);
    return posicion <= g.fin ? posicion : null;
  };
  const elegidos: number[] = [];
  grupos.forEach((g, k) => {
    const posicion = coloca(g, elegidos.at(-1));
    if (posicion !== null) {
      elegidos.push(posicion);
      return;
    }
    // El día más reciente toma el lugar de la marca anterior si no cupo (nunca el de la primera).
    if (k === grupos.length - 1 && elegidos.length > 1) {
      elegidos.pop();
      const otra = coloca(g, elegidos.at(-1));
      if (otra !== null) {
        elegidos.push(otra);
      }
    }
  });
  for (const i of elegidos) {
    marcas.set(i, etiqueta(i));
  }
  return marcas;
}

/**
 * El techo del eje: 100 en porcentajes altos; en porcentajes bajos (la
 * duplicación ronda 1 a 10 %), el escalón redondo arriba del mayor valor y de
 * la meta, para que la línea no quede pegada al piso; en conteos, un techo
 * redondo arriba del máximo.
 */
export function techoSerie(valores: (number | null)[], meta: number | null, formato: FormatoSerieCc): number {
  const mayor = Math.max(0, meta ?? 0, ...valores.map((v) => v ?? 0)) * 1.15;
  if (formato === "PCT") {
    return [5, 10, 20, 50].find((t) => mayor <= t) ?? 100;
  }
  const base = Math.max(1, mayor);
  const magnitud = Math.pow(10, Math.floor(Math.log10(base)));
  return Math.ceil(base / magnitud) * magnitud;
}

/** El valor anterior con dato antes de `i`, o null si es el primero. */
function anterior(valores: (number | null)[], i: number): number | null {
  for (let k = i - 1; k >= 0; k--) {
    const v = valores[k];
    if (v != null) {
      return v;
    }
  }
  return null;
}

/** "0.4 puntos" o "120": la magnitud de un cambio, sin signo. */
function magnitud(d: number, formato: FormatoSerieCc): string {
  const valor = Math.abs(d);
  if (formato !== "PCT") {
    return entero(valor);
  }
  const puntos = valor === 1 ? "punto" : "puntos";
  return `${pct(valor).replace(SIGNO_PCT, "")} ${puntos}`;
}

/** El tono de un cambio: mejora, empeora o se queda igual. */
function tonoCambio(d: number, menosEsMejor: boolean): { clase: string; palabra: string } {
  if (d === 0) {
    return { clase: "is-neutro", palabra: "" };
  }
  const baja = d < 0;
  const mejora = baja === menosEsMejor;
  return mejora ? { clase: "is-mejor", palabra: "mejora" } : { clase: "is-peor", palabra: "empeora" };
}

/** "Subió 0.4 puntos respecto al análisis anterior: mejora". */
export function cambioContraAnterior(s: Pick<SerieCc, "valores" | "formato" | "menosEsMejor">, i: number): LineaGlobo | null {
  const v = s.valores[i];
  if (v == null) {
    return null;
  }
  const previo = anterior(s.valores, i);
  if (previo == null) {
    return { texto: "Es el primer análisis guardado", clase: "is-neutro", icono: "flag" };
  }
  const d = Math.round((v - previo) * 10) / 10;
  if (d === 0) {
    return { texto: "Igual que en el análisis anterior", clase: "is-neutro", icono: "trending_flat" };
  }
  const tono = tonoCambio(d, s.menosEsMejor);
  return {
    texto: `${d > 0 ? "Subió" : "Bajó"} ${magnitud(d, s.formato)} respecto al análisis anterior: ${tono.palabra}`,
    clase: tono.clase,
    icono: d > 0 ? "trending_up" : "trending_down",
  };
}

/** El encabezado de cada gráfica: cuánto cambió desde el primer análisis guardado, con su tono y su explicación. */
export interface CambioSerie {
  texto: string;
  clase: string;
  icono: string;
  titulo: string;
}

/**
 * "Mejoró 92.8 puntos desde el 01/07/2025" o "Empeoró: 1,400 más desde el
 * 01/07/2025": cuánto cambió la serie desde su primer análisis guardado, con el
 * tono de si mejoró (la flecha dice si subió o bajó) y, en su title, de dónde a
 * dónde y qué es un punto. Cabe en un renglón de la tarjeta más angosta.
 */
export function cambioDesdeElPrimero(s: Pick<SerieCc, "fechas" | "valores" | "formato" | "menosEsMejor">): CambioSerie {
  const conDato = s.valores.flatMap((v, i) => (v == null ? [] : [{ v, i }]));
  if (conDato.length < 2) {
    return { texto: "Solo hay un análisis guardado", clase: "is-neutro", icono: "flag", titulo: "Hace falta otro análisis para ver cómo cambia" };
  }
  const primero = conDato[0];
  const ultimo = conDato.at(-1) ?? primero;
  const desde = fechaCorta(s.fechas[primero.i]);
  const d = Math.round((ultimo.v - primero.v) * 10) / 10;
  const deA = `Pasó de ${valorSerie(primero.v, s.formato)} el ${desde} (el primer análisis guardado) a ${valorSerie(ultimo.v, s.formato)} el ${fechaCorta(s.fechas[ultimo.i])}`;
  const punto = s.formato === "PCT" ? ". Un punto es el 1 % del código" : "";
  if (d === 0) {
    return { texto: `Igual que el ${desde}`, clase: "is-neutro", icono: "trending_flat", titulo: `${deA}${punto}` };
  }
  const tono = tonoCambio(d, s.menosEsMejor);
  const juicio = tono.palabra === "mejora" ? "Mejoró" : "Empeoró";
  const masOMenos = d > 0 ? "más" : "menos";
  const cuanto = s.formato === "PCT" ? `${juicio} ${magnitud(d, s.formato)}` : `${juicio}: ${entero(Math.abs(d))} ${masOMenos}`;
  return {
    texto: `${cuanto} desde el ${desde}`,
    clase: tono.clase,
    icono: d > 0 ? "trending_up" : "trending_down",
    titulo: `${deA}: ${d > 0 ? "subió" : "bajó"} ${magnitud(d, s.formato)}${punto}`,
  };
}

/** El resultado del Quality Gate de un análisis, en palabras. */
export function lineaGate(gate: string | null | undefined): LineaGlobo | null {
  switch (gate) {
    case "OK":
      return { texto: "Este análisis pasó el Quality Gate", clase: "is-pasa", icono: "check_circle" };
    case "ERROR":
      return { texto: "Este análisis no pasó el Quality Gate", clase: "is-no-pasa", icono: "cancel" };
    case "NONE":
      return { texto: "Este análisis no tiene resultado del Quality Gate", clase: "is-neutro", icono: "remove_circle_outline" };
    default:
      return null;
  }
}

/** "Versión 0.0 · commit a707a2e"; vacío sin ninguno de los dos. */
export function datosDelPunto(p: PuntoSerieCc | null | undefined): string {
  const partes = [p?.version ? `Versión ${p.version}` : "", p?.commit ? `commit ${p.commit}` : ""].filter((t) => t !== "");
  return partes.join(" · ");
}

/** Lo que dice el globo del punto `i`: fecha y hora, valor, cambio contra el anterior, meta, gate, versión y commit. */
export function textoGlobo(s: SerieCc, i: number): TextoGlobo {
  const v = s.valores[i];
  const unidad = s.formato === "ENTERO" && s.unidad && v != null ? ` ${s.unidad}` : "";
  let meta: LineaGlobo | null = null;
  if (s.meta != null && v != null) {
    const pide = textoMeta(s.meta, s.formato, s.sentido).replace("Meta: ", "");
    meta = cumpleMeta(v, s.meta, s.sentido)
      ? { texto: `Cumple la meta: ${pide}`, clase: "is-pasa", icono: "check" }
      : { texto: `No cumple la meta: ${pide}`, clase: "is-no-pasa", icono: "close" };
  }
  const punto = s.puntos[i];
  return {
    titulo: `Análisis del ${fechaHora(s.fechas[i])}`,
    valor: `${s.nombre}: ${valorSerie(v, s.formato, "sin dato en este análisis")}${unidad}`,
    cambio: cambioContraAnterior(s, i),
    meta,
    gate: lineaGate(punto?.gate),
    datos: datosDelPunto(punto),
  };
}
